// src/pages/Partner/PartnerCohorts.jsx

import {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  AlertTriangle,
  CheckCircle2,
  Plus,
  RefreshCw,
  Users,
} from "lucide-react";

import {
  Link,
  useOutletContext,
} from "react-router-dom";

import CreateCohortModal from "../../components/partner/CreateCohortModal";

import {
  fetchPartnerCohorts,
  fetchPartnerProgrammeAccess,
} from "../../lib/partner/partnerCohorts";


/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function formatDate(value) {
  if (!value) {
    return "Not set";
  }

  const date =
    new Date(
      `${value}T00:00:00`
    );

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return value;
  }

  return new Intl.DateTimeFormat(
    "en-ZA",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }
  ).format(date);
}


function formatDuration(
  startDate,
  endDate
) {
  if (
    !startDate &&
    !endDate
  ) {
    return "Not set";
  }

  if (
    startDate &&
    !endDate
  ) {
    return `${formatDate(
      startDate
    )} – Ongoing`;
  }

  if (
    !startDate &&
    endDate
  ) {
    return `Until ${formatDate(
      endDate
    )}`;
  }

  return `${formatDate(
    startDate
  )} – ${formatDate(
    endDate
  )}`;
}


function getJohannesburgDateKey() {
  const parts =
    new Intl.DateTimeFormat(
      "en-US",
      {
        timeZone:
          "Africa/Johannesburg",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }
    ).formatToParts(
      new Date()
    );

  const value = (type) =>
    parts.find(
      (part) =>
        part.type === type
    )?.value || "";

  return `${value(
    "year"
  )}-${value(
    "month"
  )}-${value(
    "day"
  )}`;
}


function getCohortDisplayStatus(
  cohort
) {
  const storedStatus =
    cohort?.status ||
    "draft";

  if (storedStatus !== "active") {
    return storedStatus;
  }

  const today =
    getJohannesburgDateKey();

  if (
    cohort?.end_date &&
    cohort.end_date < today
  ) {
    return "completed";
  }

  if (
    cohort?.start_date &&
    cohort.start_date > today
  ) {
    return "upcoming";
  }

  return "active";
}


function statusClasses(status) {
  switch (status) {
    case "active":
      return "bg-green-100 text-green-800";

    case "upcoming":
      return "bg-blue-100 text-blue-800";

    case "completed":
      return "bg-blue-100 text-blue-800";

    case "archived":
      return "bg-gray-200 text-gray-700";

    default:
      return "bg-amber-100 text-amber-800";
  }
}


function programmeStatusClasses(
  status
) {
  switch (status) {
    case "active":
      return "border-green-200 bg-green-50 text-green-800";

    case "suspended":
      return "border-red-200 bg-red-50 text-red-700";

    case "draft":
      return "border-amber-200 bg-amber-50 text-amber-800";

    default:
      return "border-gray-200 bg-gray-100 text-gray-700";
  }
}


function programmeStatusLabel(
  status
) {
  switch (status) {
    case "active":
      return "Active";

    case "suspended":
      return "Suspended";

    case "draft":
      return "Draft";

    case "not_configured":
      return "Not configured";

    default:
      return "Unavailable";
  }
}


function getProgrammeType(
  cohort
) {
  const value = String(
    cohort?.programme_type ||
      ""
  )
    .trim()
    .toLowerCase();

  if (!value) {
    return "Not set";
  }

  const labels = {
    incubator: "Incubator",
    accelerator: "Accelerator",
    supplier: "Supplier",
    development: "Development",
    other: "Other",
  };

  return (
    labels[value] ||
    `${value
      .charAt(0)
      .toUpperCase()}${value.slice(1)}`
  );
}


function formatCohortAllocation(
  programmeAccess
) {
  const usage =
    Number(
      programmeAccess
        ?.cohort_usage || 0
    );

  const maximum =
    programmeAccess
      ?.max_cohorts;

  if (
    maximum === null ||
    maximum === undefined
  ) {
    return `${usage} / Unlimited`;
  }

  return `${usage} / ${Number(
    maximum
  )}`;
}


function getCreationBlockLabel(
  blockCode
) {
  switch (blockCode) {
    case "cohort_limit":
      return "Cohort allocation reached";

    case "suspended":
      return "Organisation allocation suspended";

    case "draft":
      return "Organisation allocation not active";

    case "not_configured":
      return "Organisation allocation not configured";

    case "unavailable":
      return "Organisation allocation unavailable";

    default:
      return "Creation unavailable";
  }
}


function getProgrammeNotice(
  programmeAccess
) {
  const blockCode =
    programmeAccess
      ?.creation_block_code;

  switch (blockCode) {
    case "suspended":
      return {
        title:
          "Organisation allocation suspended",

        message:
          "Your existing cohorts remain available. Creating new cohorts is unavailable until the organisation allocation is reactivated.",
      };

    case "draft":
      return {
        title:
          "Organisation allocation is not active yet",

        message:
          "You can review existing cohorts, but new cohorts cannot be created while the organisation allocation is in draft.",
      };

    case "cohort_limit":
      return {
        title:
          "Cohort allocation reached",

        message:
          `You are currently using ${formatCohortAllocation(
            programmeAccess
          )} cohorts. You have reached your organisation's cohort allocation.`,
      };

    case "not_configured":
      return {
        title:
          "Organisation allocation not configured",

        message:
          "Your existing cohorts remain available, but new cohorts cannot be created until an organisation allocation has been configured.",
      };

    case "unavailable":
      return {
        title:
          "Organisation allocation unavailable",

        message:
          "Your existing cohorts remain available, but new cohort creation is currently unavailable.",
      };

    default:
      return null;
  }
}


/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

export default function PartnerCohorts() {
  const {
    access,
  } = useOutletContext();


  const [
    cohorts,
    setCohorts,
  ] = useState([]);


  const [
    loading,
    setLoading,
  ] = useState(true);


  const [
    creating,
    setCreating,
  ] = useState(false);


  const [
    error,
    setError,
  ] = useState("");


  const [
    programmeAccess,
    setProgrammeAccess,
  ] = useState(null);


  const [
    programmeLoading,
    setProgrammeLoading,
  ] = useState(true);


  const [
    programmeError,
    setProgrammeError,
  ] = useState("");


  const canManageCohorts = [
    "owner",
    "admin",
  ].includes(
    access?.partner_role
  );


  const canCreateCohort =
    canManageCohorts &&
    !programmeLoading &&
    !programmeError &&
    programmeAccess
      ?.can_create_cohort ===
      true;


  const programmeNotice =
    getProgrammeNotice(
      programmeAccess
    );


  /* ------------------------------------------------------------------------ */
  /* Load cohorts                                                              */
  /* ------------------------------------------------------------------------ */

  const loadCohorts =
    useCallback(
      async () => {
        try {
          setLoading(true);
          setError("");

          const rows =
            await fetchPartnerCohorts();

          setCohorts(
            Array.isArray(rows)
              ? rows
              : []
          );
        } catch (
          loadError
        ) {
          console.error(
            "Cohort loading failed:",
            loadError
          );

          setError(
            loadError?.message ||
              "The cohorts could not be loaded."
          );
        } finally {
          setLoading(false);
        }
      },
      []
    );


  /* ------------------------------------------------------------------------ */
  /* Load organisation allocation                                              */
  /* ------------------------------------------------------------------------ */

  const loadProgrammeAccess =
    useCallback(
      async () => {
        const partnerId =
          access?.partner_id;

        if (!partnerId) {
          setProgrammeAccess(
            null
          );

          setProgrammeError(
            "The partner organisation could not be identified."
          );

          setProgrammeLoading(
            false
          );

          return;
        }


        try {
          setProgrammeLoading(
            true
          );

          setProgrammeError(
            ""
          );


          const result =
            await fetchPartnerProgrammeAccess(
              partnerId
            );


          setProgrammeAccess(
            result
          );
        } catch (
          loadError
        ) {
          console.error(
            "Organisation allocation loading failed:",
            loadError
          );


          setProgrammeAccess(
            null
          );


          setProgrammeError(
            loadError?.message ||
              "Organisation allocation could not be checked."
          );
        } finally {
          setProgrammeLoading(
            false
          );
        }
      },
      [
        access?.partner_id,
      ]
    );


  useEffect(() => {
    loadCohorts();
  }, [
    loadCohorts,
  ]);


  useEffect(() => {
    loadProgrammeAccess();
  }, [
    loadProgrammeAccess,
  ]);


  async function handleCreated() {
    await Promise.all([
      loadCohorts(),
      loadProgrammeAccess(),
    ]);
  }


  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <>
      {/* ================================================================ */}
      {/* Header                                                           */}
      {/* ================================================================ */}

      <header className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">

        <div>
          <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
            Partner · Cohorts
          </p>

          <h1 className="mt-2 text-[34px] font-black tracking-[-0.03em] md:text-[42px]">
            Cohorts
          </h1>

          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[#757575] md:text-base">
            Create and manage learner
            groups connected to{" "}
            {access.partner_name}.
          </p>
        </div>


        {canManageCohorts && (
          <div className="flex flex-col items-stretch gap-2 sm:items-end">

            <button
              type="button"
              onClick={() => {
                if (
                  canCreateCohort
                ) {
                  setCreating(
                    true
                  );
                }
              }}
              disabled={
                !canCreateCohort
              }
              title={
                canCreateCohort
                  ? "Create a new cohort"
                  : programmeLoading
                  ? "Checking organisation allocation"
                  : programmeError
                  ? "Organisation allocation could not be checked"
                  : getCreationBlockLabel(
                      programmeAccess
                        ?.creation_block_code
                    )
              }
              className={[
                "inline-flex items-center justify-center rounded-full px-5 py-3 text-sm font-black transition",
                canCreateCohort
                  ? "bg-[#FF914D] text-white hover:bg-[#E9782F]"
                  : "cursor-not-allowed bg-[#E7E7E7] text-[#8A8A8A]",
              ].join(" ")}
            >
              <Plus className="mr-2 h-4 w-4" />

              Create cohort
            </button>


            {!canCreateCohort &&
              !programmeLoading &&
              !programmeError &&
              programmeAccess
                ?.creation_block_code &&
              programmeAccess
                .creation_block_code !==
                "not_permitted" && (
                <p className="text-xs font-bold text-[#757575]">
                  {getCreationBlockLabel(
                    programmeAccess
                      .creation_block_code
                  )}
                </p>
              )}

          </div>
        )}

      </header>


      {/* ================================================================ */}
      {/* Organisation allocation                                           */}
      {/* ================================================================ */}

      {programmeLoading ? (
        <section className="mt-6 rounded-[24px] border border-[#EFEFEF] bg-white p-5 shadow-sm">
          <p className="text-sm font-semibold text-[#757575]">
            Checking organisation
            allocation…
          </p>
        </section>
      ) : programmeError ? (
        <section className="mt-6 rounded-[24px] border border-amber-200 bg-amber-50 p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">

            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 flex-none text-amber-700" />

              <div>
                <h2 className="text-sm font-black text-amber-900">
                  Organisation allocation
                  could not be checked
                </h2>

                <p className="mt-1 text-sm leading-relaxed text-amber-800">
                  Existing cohorts remain
                  available, but new cohort
                  creation is temporarily
                  unavailable until organisation
                  allocation can be verified.
                </p>
              </div>
            </div>


            <button
              type="button"
              onClick={
                loadProgrammeAccess
              }
              className="inline-flex flex-none items-center justify-center rounded-full border border-amber-300 bg-white px-4 py-2 text-xs font-black text-amber-800 transition hover:bg-amber-100"
            >
              <RefreshCw className="mr-2 h-4 w-4" />

              Retry
            </button>

          </div>
        </section>
      ) : programmeAccess ? (
        <section className="mt-6 rounded-[24px] border border-[#EFEFEF] bg-white p-5 shadow-sm">

          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">

            <div>
              <p className="text-xs font-black uppercase tracking-wider text-[#757575]">
                Organisation allocation
              </p>

              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span
                  className={[
                    "inline-flex rounded-full border px-3 py-1 text-xs font-black",
                    programmeStatusClasses(
                      programmeAccess
                        .effective_status
                    ),
                  ].join(" ")}
                >
                  {programmeStatusLabel(
                    programmeAccess
                      .effective_status
                  )}
                </span>

                {programmeAccess
                  .effective_status ===
                  "active" &&
                  canCreateCohort && (
                    <span className="inline-flex items-center text-xs font-bold text-green-700">
                      <CheckCircle2 className="mr-1.5 h-4 w-4" />

                      Cohort creation available
                    </span>
                  )}
              </div>
            </div>


            <div className="sm:text-right">
              <p className="text-xs font-black uppercase tracking-wider text-[#757575]">
                Cohort allocation
              </p>

              <p className="mt-2 text-lg font-black text-[#333333]">
                {formatCohortAllocation(
                  programmeAccess
                )}
              </p>

              <p className="mt-1 text-xs text-[#757575]">
                Used / allocation
              </p>
            </div>

          </div>


          {programmeNotice && (
            <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4">

              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 flex-none text-amber-700" />

                <div>
                  <h3 className="text-sm font-black text-amber-900">
                    {programmeNotice.title}
                  </h3>

                  <p className="mt-1 text-sm font-semibold leading-relaxed text-amber-800">
                    {programmeNotice.message}
                  </p>
                </div>
              </div>

            </div>
          )}

        </section>
      ) : null}


      {/* ================================================================ */}
      {/* Error                                                            */}
      {/* ================================================================ */}

      {error && (
        <div
          role="alert"
          className="mt-6 flex items-center justify-between gap-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700"
        >
          <span>
            {error}
          </span>

          <button
            type="button"
            onClick={
              loadCohorts
            }
            className="inline-flex flex-none items-center rounded-full border border-red-300 px-3 py-2"
          >
            <RefreshCw className="mr-2 h-4 w-4" />

            Retry
          </button>
        </div>
      )}


      {/* ================================================================ */}
      {/* Loading                                                          */}
      {/* ================================================================ */}

      {loading ? (
        <div className="mt-7 rounded-[24px] border border-[#EFEFEF] bg-white p-8 text-center text-sm font-semibold text-[#757575]">
          Loading cohorts…
        </div>
      ) : cohorts.length ===
        0 ? (

        /* ============================================================ */
        /* Empty                                                        */
        /* ============================================================ */

        <section className="mt-7 rounded-[28px] border border-dashed border-[#FFB98B] bg-white p-8 text-center shadow-sm">

          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
            <Users className="h-7 w-7" />
          </div>

          <h2 className="mt-5 text-xl font-black">
            No cohorts yet
          </h2>

          <p className="mx-auto mt-2 max-w-lg text-sm leading-relaxed text-[#757575]">
            {canCreateCohort
              ? "Create the first cohort for this partner organisation."
              : canManageCohorts
              ? "There are no cohorts yet. New cohort creation is currently unavailable."
              : "There are no cohorts available for you to view yet."}
          </p>


          {canManageCohorts && (
            <button
              type="button"
              onClick={() => {
                if (
                  canCreateCohort
                ) {
                  setCreating(
                    true
                  );
                }
              }}
              disabled={
                !canCreateCohort
              }
              className={[
                "mt-6 inline-flex items-center rounded-full px-5 py-3 text-sm font-black transition",
                canCreateCohort
                  ? "bg-[#FF914D] text-white hover:bg-[#E9782F]"
                  : "cursor-not-allowed bg-[#E7E7E7] text-[#8A8A8A]",
              ].join(" ")}
            >
              <Plus className="mr-2 h-4 w-4" />

              Create first cohort
            </button>
          )}

        </section>
      ) : (

        /* ============================================================ */
        /* Cohort cards                                                 */
        /* ============================================================ */

        <section className="mt-7 grid gap-4 md:grid-cols-2 xl:grid-cols-3">

          {cohorts.map(
            (cohort) => {
              const displayStatus =
                getCohortDisplayStatus(
                  cohort
                );

              return (
              <article
                key={
                  cohort.id
                }
                className="flex flex-col rounded-[24px] border border-[#EFEFEF] bg-white p-5 shadow-sm"
              >

                {/* ---------------------------------------------------- */}
                {/* Status + title                                       */}
                {/* ---------------------------------------------------- */}

                <div>
                  <span
                    className={[
                      "inline-flex rounded-full px-3 py-1 text-xs font-black capitalize",

                      statusClasses(
                        displayStatus
                      ),
                    ].join(" ")}
                  >
                    {displayStatus}
                  </span>


                  <h2 className="mt-4 text-xl font-black">
                    {cohort.name}
                  </h2>
                </div>



                {/* ---------------------------------------------------- */}
                {/* Metrics                                              */}
                {/* ---------------------------------------------------- */}

                <div className="mt-5 space-y-3 border-t border-[#EFEFEF] pt-4 text-sm">

                  {/* Programme Type */}

                  <CohortMetric
                    label="Programme type"
                    value={
                      getProgrammeType(
                        cohort
                      )
                    }
                  />


                  {/* Duration */}

                  <CohortMetric
                    label="Duration"
                    value={
                      formatDuration(
                        cohort.start_date,
                        cohort.end_date
                      )
                    }
                    valueClassName="max-w-[62%] text-right"
                  />


                  {/* Cohort Size */}

                  <CohortMetric
                    label="Cohort size"
                    value={
                      cohort.capacity
                        ? Number(
                            cohort.capacity
                          )
                        : "Unlimited"
                    }
                  />


                  {/* Active Learners */}

                  <CohortMetric
                    label="Active learners"
                    value={Number(
                      cohort.active_learner_count ||
                        0
                    )}
                  />


                  {/* Pending Invitations */}

                  <CohortMetric
                    label="Pending invitations"
                    value={Number(
                      cohort.pending_invitation_count ||
                        0
                    )}
                  />

                </div>


                {/* ---------------------------------------------------- */}
                {/* View                                                 */}
                {/* ---------------------------------------------------- */}

                <Link
                  to={`/partner/cohorts/${cohort.id}`}
                  className="mt-5 inline-flex w-full items-center justify-center rounded-full border border-[#FF914D] px-4 py-2.5 text-sm font-black text-[#FF914D] transition hover:bg-[#FF914D] hover:text-white"
                >
                  View cohort
                </Link>

              </article>
              );
            }
          )}

        </section>
      )}


      {/* ================================================================ */}
      {/* Create modal                                                     */}
      {/* ================================================================ */}

      <CreateCohortModal
        open={creating}
        partnerId={
          access.partner_id
        }
        onClose={() =>
          setCreating(false)
        }
        onCreated={
          handleCreated
        }
      />
    </>
  );
}


/* -------------------------------------------------------------------------- */
/* Metric row                                                                 */
/* -------------------------------------------------------------------------- */

function CohortMetric({
  label,
  value,
  valueClassName = "",
}) {
  return (
    <div className="flex items-start justify-between gap-4">

      <span className="text-[#757575]">
        {label}
      </span>

      <span
        className={[
          "font-black text-black",
          valueClassName,
        ].join(" ")}
      >
        {value}
      </span>

    </div>
  );
}